import { FC, memo } from 'react';
import Link from 'next/link';
import { FaCheck } from 'react-icons/fa6';
import { FiX } from 'react-icons/fi';

import { AuthIterationType } from '../types';
import { sophiPaywallCopyMap, EdgeLogoSvg } from '.';
import { useIsUserLoggedIn } from '@benzinga/user-context';
import { usePaywallTracking } from '../../../hooks/usePaywallTracking';
import { buildCheckoutUrl, getPaywallConfig, handlePaywallLoginRedirect } from '../../../utils/paywallUtils';

import '../paywall.scss';

import bearImg from './assets/poly-bear.png';
import bullImg from './assets/poly-bull.png';
import Image from 'next/image';

interface OptimizelyPaywallProps extends AuthIterationType {
  isMobile?: boolean;
  paywallId?: string;
}

export const OptimizelyPaywall: FC<OptimizelyPaywallProps> = memo(
  ({ allowClose, isMobile, placement, setShowPaywall }) => {
    const adType = allowClose ? 'soft' : 'hard';
    const adScreen = isMobile ? 'mo' : 'de';
    const adId = `${adType}-${adScreen}-optimizely`;
    const paywallId = 'ct-pw1'; // use copy from ct-pw1 as default
    const { hasAltCheckout, paywallCopy, utmSource } = getPaywallConfig(
      paywallId,
      sophiPaywallCopyMap,
      'optimizely',
      'optimizely',
    );

    const campaign = 'command-center-optimizely';

    const checkoutUrl = buildCheckoutUrl({
      ad: adId,
      adType: 'paywall',
      campaign,
      hasAltCheckout,
    });

    const listItems = paywallCopy?.listItems ?? [];
    const isLoggedIn = useIsUserLoggedIn();

    usePaywallTracking({
      allowClose,
      checkoutUrl,
      placement,
      utmSource,
    });

    const handleLoginRedirect = () => handlePaywallLoginRedirect(utmSource);

    return (
      <>
        <div className="fixed inset-0 z-[1000002] touch-none pointer-events-none"></div>
        <div
          className={
            'paywall-content w-full max-h-[100vh] h-auto animate-fade-up animate-delay-300 animate-once no-scrollbar overflow-scroll flex flex-col md:flex-row items-center md:items-start justify-start md:justify-center md:gap-12 fixed z-[1000003] bottom-0 left-0 bg-white border-t ' +
            'paywall-wrapper'
          }
        >
          {allowClose && setShowPaywall && (
            <button
              className="absolute top-2 right-4 text-black z-10"
              onClick={() => {
                setShowPaywall(false);
              }}
            >
              <FiX />
            </button>
          )}
          <Link className={'w-full flex flex-col overflow-hidden paywall-link'} href={checkoutUrl} target="_blank">
            <div className={'paywall-logo-wrapper'}>
              <EdgeLogoSvg />
              <span>exclusive</span>
            </div>
            <div className={'content-wrapper'}>
              {paywallCopy?.title && <h2 className={'paywall-title'}>{paywallCopy.title}</h2>}
              <div className={'paywall-list-wrapper'}>
                {paywallCopy?.listHeading && <h3 className={'paywall-list-heading'}>{paywallCopy.listHeading}</h3>}
                <ul className={'paywall-list'}>
                  {listItems.map((item, index) => (
                    <li className={'paywall-list-item'} key={index}>
                      <FaCheck className={'paywall-check-icon'} size={9} />
                      <div>{item}</div>
                    </li>
                  ))}
                </ul>
                <div className={'paywall-button'}>
                  {isMobile && paywallCopy?.buttonMobile ? paywallCopy.buttonMobile : paywallCopy?.button}
                </div>
              </div>
            </div>
            {!isLoggedIn && (
              <div className={'auth-redirect-footer'} onClick={handleLoginRedirect}>
                Already a member? <u>Log in</u>
              </div>
            )}
            <div className="absolute bottom-0 w-full flex flex-row justify-between">
              <Image alt="poly bear" className={'desktop-animal'} height={798} src={bearImg} width={524}></Image>
              <Image alt="poly bull" className={'desktop-animal'} height={798} src={bullImg} width={524}></Image>
            </div>
          </Link>
        </div>
      </>
    );
  },
);

OptimizelyPaywall.displayName = 'OptimizelyPaywall';
